import React from 'react';
import TipTapScopeNotes from './TipTapScopeNotes';
import { emptyTipTapNotes } from './tipTapEmpty';
import {
  isEmptyAnswerNumber,
  normalizeFtSpacing,
} from './scopeNumberCoercion';

/** Match Lost & Found / app form screens */
export const LABEL_CLASS =
  'block text-sm font-medium text-black mb-2 font-["Open_Sans"]';

export const OPTION_CLASS = 'text-sm text-black font-["Open_Sans"] leading-snug';

export const FIELD_CLASS =
  'w-full rounded-xl border border-zinc-300 bg-white px-4 py-3 text-base text-black font-["Open_Sans"] placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#6BABA0]/40 focus:border-[#6BABA0]';

export const FIELD_CLASS_READONLY =
  'w-full rounded-xl border border-zinc-200 bg-gray-50 px-4 py-3 text-base text-black font-["Open_Sans"]';

export const SUBSECTION_TITLE_CLASS =
  'text-lg font-semibold text-black font-["Open_Sans"] mb-1';

export const BODY_TEXT_CLASS = 'text-sm text-gray-600 font-["Open_Sans"]';

export const NAV_ITEM_CLASS =
  'text-sm font-medium text-black font-["Open_Sans"] truncate';

export const DETAIL_LABEL_CLASS =
  'block text-sm font-medium text-black mb-1 font-["Open_Sans"]';

export const DETAIL_VALUE_CLASS = 'text-base text-black font-["Open_Sans"]';

export const titleCaseLabel = (value = '') =>
  String(value)
    .split(' ')
    .map((word) => {
      if (!word) return word;
      if (word === '&' || word === '—') return word;
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(' ');

const noop = () => {};

const toTipTapValue = (value) => {
  if (value && typeof value === 'object' && value.format === 'tiptap') {
    return value;
  }
  if (value && typeof value === 'object' && value.type === 'doc') {
    return { format: 'tiptap', doc: value };
  }
  return emptyTipTapNotes();
};

const ScopeField = ({ field, value, onChange = noop, readOnly = false }) => {
  if (!field) return null;

  const label = titleCaseLabel(field.label);
  const inputClass = readOnly ? FIELD_CLASS_READONLY : FIELD_CLASS;

  if (field.type === 'richNotes' || field.type === 'notes') {
    return (
      <TipTapScopeNotes
        label={label}
        value={toTipTapValue(value)}
        onChange={readOnly ? noop : onChange}
        readOnly={readOnly}
        minHeight={field.minHeight || 120}
      />
    );
  }

  if (field.type === 'text' || field.type === 'number') {
    const displayValue =
      field.type === 'number' && isEmptyAnswerNumber(value)
        ? ''
        : value ?? '';
    return (
      <div>
        <label className={LABEL_CLASS}>{label}</label>
        <input
          type={field.type === 'number' ? 'number' : 'text'}
          min={
            field.min != null
              ? field.min
              : field.type === 'number'
                ? '0'
                : undefined
          }
          max={field.max != null ? field.max : undefined}
          value={displayValue}
          onChange={(e) => {
            if (field.type === 'number') {
              const raw = e.target.value;
              onChange(raw === '' ? '' : Number(raw));
              return;
            }
            onChange(e.target.value);
          }}
          placeholder={field.placeholder || (readOnly ? '—' : '')}
          readOnly={readOnly}
          disabled={readOnly}
          className={`${inputClass} ${readOnly ? 'cursor-default' : ''}`}
        />
      </div>
    );
  }

  if (field.type === 'checkbox' || field.type === 'boolean') {
    const checked = value === true || value === 'Yes' || value === 'yes';
    return (
      <label
        className={`inline-flex items-start gap-2 rounded-lg border px-3 py-2 transition-colors ${
          readOnly ? 'cursor-default' : 'cursor-pointer'
        } ${
          checked
            ? 'border-[#6BABA0] bg-teal-50'
            : 'border-zinc-200 bg-white hover:bg-gray-50'
        }`}
      >
        <input
          type="checkbox"
          checked={checked}
          disabled={readOnly}
          onChange={(e) => onChange(e.target.checked)}
          className="mt-1 accent-[#0A8089]"
        />
        <span className={OPTION_CLASS}>{label}</span>
      </label>
    );
  }

  if (field.type === 'yesNo' || field.type === 'singleSelect') {
    const options = field.options || [];
    // Legacy checkboxGroup storage may leave ["Option"] — treat as single value.
    const selectedValue = Array.isArray(value)
      ? value.find((v) => v != null && String(v).trim() !== '') ?? ''
      : value;
    const selectedNorm =
      selectedValue == null || selectedValue === ''
        ? ''
        : normalizeFtSpacing(selectedValue);
    return (
      <fieldset>
        <legend className={LABEL_CLASS}>{label}</legend>
        <div className="flex flex-wrap gap-3">
          {options.map((option) => {
            const selected =
              selectedValue === option ||
              (selectedNorm && selectedNorm === normalizeFtSpacing(option));
            return (
              <label
                key={option}
                className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 transition-colors ${
                  readOnly ? 'cursor-default' : 'cursor-pointer'
                } ${
                  selected
                    ? 'border-[#6BABA0] bg-teal-50'
                    : 'border-zinc-200 bg-white hover:bg-gray-50'
                } ${readOnly && !selected ? 'opacity-50' : ''}`}
              >
                <input
                  type="radio"
                  name={`${field.id}-${field.label}`}
                  checked={selected}
                  onChange={() => onChange(option)}
                  disabled={readOnly}
                  className="accent-[#0A8089]"
                />
                <span className={OPTION_CLASS}>{option}</span>
              </label>
            );
          })}
        </div>
      </fieldset>
    );
  }

  if (field.type === 'checkboxGroup') {
    const selected = Array.isArray(value)
      ? value
      : value != null && String(value).trim() !== ''
        ? [String(value)]
        : [];
    const options = field.options || [];
    return (
      <fieldset>
        <legend className={LABEL_CLASS}>{label}</legend>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {options.map((option) => {
            const checked = selected.includes(option);
            return (
              <label
                key={option}
                className={`inline-flex items-start gap-2 rounded-lg border px-3 py-2 transition-colors ${
                  readOnly ? 'cursor-default' : 'cursor-pointer'
                } ${
                  checked
                    ? 'border-[#6BABA0] bg-teal-50'
                    : 'border-zinc-200 bg-white hover:bg-gray-50'
                } ${readOnly && !checked ? 'opacity-50' : ''}`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={readOnly}
                  onChange={() => {
                    if (checked) {
                      onChange(selected.filter((item) => item !== option));
                    } else {
                      onChange([...selected, option]);
                    }
                  }}
                  className="mt-1 accent-[#0A8089]"
                />
                <span className={OPTION_CLASS}>{option}</span>
              </label>
            );
          })}
        </div>
      </fieldset>
    );
  }

  return null;
};

export default ScopeField;
